"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import {
  buildMowingPlan,
  M_GRASSES,
  M_MOWERS,
  M_SEASONS,
  MOW_GRASS_NAME_TO_ID,
  resolveMowGrassId,
  type MowGrassId,
  type MowSeasonId,
  type MowerTypeId,
} from "@/lib/mowing-plan"
import { calculateMowingRecommendations, type MowingResult } from "@/lib/mowing-calculator"
import { useSavedInputs } from "@/hooks/use-saved-inputs"
import { getRegionFromZipCode, REGION_DEFAULT_GRASS, toLegacyRegion } from "@/data/regional-climate"
import { ZipCodeInput } from "@/components/ui/zip-code-input"
import { MowingPreview } from "@/components/tools/mowing-preview"
import { getCurrentSeason } from "@/hooks/use-default-lawn"
import { grassDisplayName } from "@/lib/grass-display"

const M_BG = "#ffffff"
const M_INK = "#111827"
const M_INK2 = "#374151"
const M_RULE = "#e5e7eb"
const M_BRAND = "#16a34a"
const M_BRAND_700 = "#15803d"
const M_BRAND_50 = "#f0fdf4"
const M_BRAND_100 = "#dcfce7"
const M_MUTED = "#6b7280"
const M_PAPER_DEEP = "#f3f4f6" // gray-100 — faint backdrop behind the carded sections
const M_FONT = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`

// ── Inline icons ──────────────────────────────────────────────────
function Ico({ size = 14, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}
const IcoLeaf = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M5 19c8 0 14-6 14-14C9 5 5 11 5 19z" />
    <path d="M5 19l9-9" />
  </Ico>
)
const IcoSun = (p: { size?: number }) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4" />
  </Ico>
)
const IcoPin = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M12 21s-7-7.5-7-12a7 7 0 0114 0c0 4.5-7 12-7 12z" />
    <circle cx="12" cy="9" r="2.5" />
  </Ico>
)
const IcoMower = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M3 17h12l3-3h3v3M5 17a2 2 0 100 4 2 2 0 000-4zM15 17a2 2 0 100 4 2 2 0 000-4z" />
    <path d="M8 11l2-3M11 11l1-3M14 11l1-3" />
  </Ico>
)
const IcoChev = (p: { size?: number }) => <Ico {...p}><path d="M6 9l6 6 6-6" /></Ico>
const IcoLock = (p: { size?: number }) => (
  <Ico {...p}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 018 0v4" />
  </Ico>
)

function GrassGlyph({ active, season }: { active: boolean; season: "warm" | "cool" }) {
  const ink = active ? M_BRAND : M_INK2
  return (
    <span
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        background: active ? M_BRAND_100 : M_BRAND_50,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ink}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 21h16" />
        <path d="M7 21c0-5 1-8 3-10" />
        <path d="M12 21c0-6 1-9 3-12" />
        <path d="M17 21c0-4 0-7 2-9" />
        {season === "cool" && <circle cx="6" cy="9" r="1.2" fill={ink} stroke="none" />}
      </svg>
    </span>
  )
}

function StepCircle({ step, accent }: { step: number; accent?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 27,
        height: 27,
        flexShrink: 0,
        borderRadius: "50%",
        background: accent ? M_BRAND_50 : M_PAPER_DEEP,
        border: accent ? `1.5px solid ${M_BRAND}` : `1px solid ${M_RULE}`,
        boxShadow: accent ? `0 0 0 3px ${M_BRAND}22` : "none",
        color: accent ? M_BRAND_700 : M_INK,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {step}
    </span>
  )
}

function Label({
  icon,
  children,
  hint,
  step,
  accent,
}: {
  icon?: React.ReactNode
  children: React.ReactNode
  hint?: React.ReactNode
  step?: number
  accent?: boolean
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        marginBottom: 10,
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {step != null && <StepCircle step={step} accent={accent} />}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: M_BRAND_700,
          }}
        >
          {icon}
          {children}
        </div>
      </div>
      {hint && <div style={{ fontSize: 13, color: M_MUTED, textAlign: "right" }}>{hint}</div>}
    </div>
  )
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${M_RULE}`,
        borderRadius: 12,
        padding: 18,
      }}
    >
      {children}
    </div>
  )
}

const GRASS_RECOMMENDED: MowGrassId[] = ["bermuda", "zoysia", "fescue", "kbg"]

function GrassField({
  value,
  onChange,
  season,
}: {
  value: MowGrassId
  onChange: (v: MowGrassId) => void
  season: MowSeasonId
}) {
  const [showAll, setShowAll] = useState(false)
  const all = Object.keys(M_GRASSES) as MowGrassId[]
  const list = showAll ? all : GRASS_RECOMMENDED
  const cur = M_GRASSES[value]
  return (
    <div>
      <Label step={2} icon={<IcoLeaf size={13} />} hint={`${cur.height[season]}″ target`}>
        Grass type
      </Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
        {list.map((id) => {
          const g = M_GRASSES[id]
          const active = id === value
          return (
            <button
              type="button"
              key={id}
              onClick={() => onChange(id)}
              aria-pressed={active}
              style={{
                padding: "10px 12px",
                background: active ? M_BRAND_50 : "#fff",
                color: M_INK,
                border: `1px solid ${active ? M_BRAND : M_RULE}`,
                borderRadius: 8,
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span className="hidden xs:inline-flex">
                <GrassGlyph active={active} season={g.season} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    overflowWrap: "break-word",
                  }}
                >
                  {grassDisplayName(g.name)}
                </div>
                <div style={{ fontSize: 12, color: M_MUTED, marginTop: 1 }}>
                  {g.tag} · {g.height[season]}″ in {season}
                </div>
              </div>
            </button>
          )
        })}
      </div>
      {!showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{
            marginTop: 10,
            background: "transparent",
            border: "none",
            padding: "4px 0",
            fontSize: 13,
            color: M_BRAND,
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          +{all.length - GRASS_RECOMMENDED.length} more grass types <IcoChev size={12} />
        </button>
      )}
    </div>
  )
}

function SeasonField({
  value,
  onChange,
}: {
  value: MowSeasonId
  onChange: (v: MowSeasonId) => void
}) {
  return (
    <div>
      <Label step={3} icon={<IcoSun size={13} />} hint="auto-detected">
        Season
      </Label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          background: "#fff",
          border: `1px solid ${M_RULE}`,
          borderRadius: 8,
          padding: 4,
          gap: 4,
        }}
      >
        {M_SEASONS.map((s) => {
          const active = s.id === value
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => onChange(s.id)}
              aria-pressed={active}
              style={{
                padding: "10px 6px",
                background: active ? M_BRAND : "transparent",
                color: active ? "#fff" : M_INK,
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {s.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function MowerField({
  value,
  onChange,
  narrow,
}: {
  value: MowerTypeId
  onChange: (v: MowerTypeId) => void
  narrow: boolean
}) {
  return (
    <div>
      <Label step={4} icon={<IcoMower size={13} />}>Mower type</Label>
      <div style={{ display: "grid", gridTemplateColumns: narrow ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: 8 }}>
        {(Object.entries(M_MOWERS) as [MowerTypeId, (typeof M_MOWERS)[MowerTypeId]][]).map(
          ([id, m]) => {
            const active = id === value
            return (
              <button
                type="button"
                key={id}
                onClick={() => onChange(id)}
                aria-pressed={active}
                style={{
                  padding: "12px 12px",
                  background: active ? M_BRAND_50 : "#fff",
                  color: M_INK,
                  border: `1px solid ${active ? M_BRAND : M_RULE}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600 }}>{m.name}</div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: M_MUTED,
                    marginTop: 2,
                    lineHeight: 1.4,
                  }}
                >
                  {m.blurb}
                </div>
              </button>
            )
          },
        )}
      </div>
    </div>
  )
}

// ── Form ──────────────────────────────────────────────────────────
const M_REGION_TO_GRASS: Record<string, MowGrassId> = {
  Southeast: "bermuda",
  "South Central": "bermuda",
  Northeast: "fescue",
  "Great Lakes": "kbg",
  "Upper Midwest": "kbg",
  "Mountain West": "kbg",
  "Mid-Atlantic": "fescue",
  Central: "fescue",
  "West Coast": "fescue",
}

interface MowingFormProps {
  initialValues?: {
    zipCode?: string
    grassType?: string
    sunlight?: string
    season?: string
    mowerType?: string
  }
  error?: string | null
  /** Called whenever the live calculated result changes, so the page can render
   * the result card and downstream UI without duplicating the calculation. */
  onResultChange?: (result: MowingResult | null) => void
  onMowerChange?: (mowerType: MowerTypeId) => void
}

export function MowingForm({ initialValues, error = null, onResultChange, onMowerChange }: MowingFormProps) {
  const [zip, setZip] = useState("")
  const [grass, setGrass] = useState<MowGrassId>("bermuda")
  const initialSeason = (() => {
    const cs = (getCurrentSeason() as string).toLowerCase()
    if (cs === "spring" || cs === "summer" || cs === "fall" || cs === "winter") {
      return cs as MowSeasonId
    }
    return "spring"
  })()
  const [season, setSeason] = useState<MowSeasonId>(initialSeason)
  const [mower, setMower] = useState<MowerTypeId>("rotary")

  const region = useMemo(() => getRegionFromZipCode(zip), [zip])
  const zipKnown = zip.length === 5 && region !== "Unknown"

  const { savedInputs, saveInputs } = useSavedInputs()
  const seededFromZip = useRef(false)
  const seededFromSaved = useRef(false)
  const userEdited = useRef({ zip: false, grass: false, season: false, mower: false })

  useEffect(() => {
    if (initialValues?.zipCode && /^\d{5}$/.test(initialValues.zipCode) && !userEdited.current.zip) {
      setZip(initialValues.zipCode)
    }
    const initialGrassId = resolveMowGrassId(initialValues?.grassType)
    if (initialGrassId && !userEdited.current.grass) {
      setGrass(initialGrassId)
      seededFromZip.current = true
      seededFromSaved.current = true
    }
    if (
      initialValues?.season &&
      ["spring", "summer", "fall", "winter"].includes(initialValues.season) &&
      !userEdited.current.season
    ) {
      setSeason(initialValues.season as MowSeasonId)
    }
    if (
      initialValues?.mowerType &&
      ["rotary", "reel", "robotic"].includes(initialValues.mowerType) &&
      !userEdited.current.mower
    ) {
      setMower(initialValues.mowerType as MowerTypeId)
    }
  }, [initialValues])

  // Seed grass from cross-site savedInputs *before* zip-based defaults so a
  // user-chosen grass elsewhere on the site overrides regional defaults.
  useEffect(() => {
    if (seededFromSaved.current) return
    const savedGrassId = resolveMowGrassId(savedInputs.grassType)
    if (savedGrassId) {
      setGrass(savedGrassId)
      seededFromZip.current = true
      seededFromSaved.current = true
    }
  }, [savedInputs.grassType])

  useEffect(() => {
    if (!zipKnown || seededFromZip.current) return
    const def = REGION_DEFAULT_GRASS[region]
    if (def && MOW_GRASS_NAME_TO_ID[def]) {
      setGrass(MOW_GRASS_NAME_TO_ID[def])
    } else if (M_REGION_TO_GRASS[toLegacyRegion(region)]) {
      // M_REGION_TO_GRASS is keyed on the legacy region set; degrade fine-grained
      // climate sub-regions (California, Florida, etc.) to their legacy parent.
      setGrass(M_REGION_TO_GRASS[toLegacyRegion(region)])
    }
    seededFromZip.current = true
  }, [zipKnown, region])

  const plan = useMemo(
    () => buildMowingPlan({ grass, season, mower }),
    [grass, season, mower],
  )

  // ── Live calculation (re-runs on every state change) ─────────────
  // The mowing recommendation depends only on grass, season, and sunlight —
  // not ZIP — so it computes unconditionally (every input has a default) and
  // streams up to the page via onResultChange, matching the watering tool.
  const sunlight = initialValues?.sunlight ?? savedInputs.sunlight
  const liveResult: MowingResult | null = useMemo(() => {
    try {
      const engineResult = calculateMowingRecommendations(M_GRASSES[grass].engineKey, season, sunlight)
      const height = `${plan.idealH.toFixed(2).replace(/\.?0+$/, "")} inches`
      return {
        ...engineResult,
        currentRecommendations: {
          ...engineResult.currentRecommendations,
          height,
          frequency: `Every ${plan.freqDays} days`,
        },
      }
    } catch {
      return null
    }
  }, [grass, season, sunlight, plan])

  useEffect(() => {
    onResultChange?.(liveResult)
  }, [liveResult, onResultChange])

  useEffect(() => {
    onMowerChange?.(mower)
  }, [mower, onMowerChange])

  // Stacked layout through tablet portrait; the desktop preview rail needs room.
  const [narrow, setNarrow] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)")
    const sync = () => setNarrow(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  return (
    // Kept as a <form> (with no submit UI) so the postal-code input keeps its
    // accessible grouping + browser autofill context, matching the watering form.
    <form
      onSubmit={(e) => e.preventDefault()}
      className="overflow-hidden rounded-2xl border border-gray-200 shadow-elevation-2"
      style={{
        background: M_BG,
        color: M_INK,
        fontFamily: M_FONT,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: narrow ? "minmax(0,1fr)" : "minmax(0,1fr) 440px",
          gap: narrow ? 24 : 32,
          padding: narrow ? "16px" : "32px 32px 36px",
        }}
      >
        {narrow && <MowingPreview plan={plan} compact />}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            background: M_PAPER_DEEP,
            borderRadius: 14,
            padding: 12,
          }}
        >
          <SectionCard>
            <Label
              step={1}
              accent
              icon={<IcoPin size={13} />}
              hint={zipKnown ? `climate zone matched: ${region}` : "sets your climate zone"}
            >
              Where
            </Label>
            <ZipCodeInput
              value={zip}
              onChange={(next) => {
                userEdited.current.zip = true
                setZip(next)
              }}
              showLabel={false}
              enableGeolocation
              rememberLastZip
            />
          </SectionCard>

          <SectionCard>
            <GrassField
              value={grass}
              onChange={(g) => {
                userEdited.current.grass = true
                seededFromZip.current = true
                setGrass(g)
                saveInputs({ grassType: M_GRASSES[g].engineKey })
              }}
              season={season}
            />
          </SectionCard>

          <SectionCard>
            <SeasonField
              value={season}
              onChange={(next) => {
                userEdited.current.season = true
                setSeason(next)
              }}
            />
          </SectionCard>

          <SectionCard>
            <MowerField
              value={mower}
              onChange={(next) => {
                userEdited.current.mower = true
                setMower(next)
              }}
              narrow={narrow}
            />
          </SectionCard>

          {error && (
            <div
              role="alert"
              style={{
                fontSize: 13,
                color: "#b04531",
                background: "#fff5f3",
                border: "1px solid #f5d4cd",
                borderRadius: 8,
                padding: "10px 12px",
              }}
            >
              {error}
            </div>
          )}
        </div>

        {!narrow && (
          <aside
            style={{
              alignSelf: "flex-start",
              position: "sticky",
              top: 24,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <MowingPreview plan={plan} />
            <div
              style={{
                fontSize: 12,
                color: M_MUTED,
                lineHeight: 1.55,
                display: "flex",
                gap: 8,
                padding: "0 4px",
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 2 }}>
                <IcoLock size={12} />
              </span>
              <span>From university extension data, last reviewed May 2026.</span>
            </div>
          </aside>
        )}
      </div>
    </form>
  )
}
